import type { Beatmap } from "./beatmap";
import dojo01 from "../beatmaps/dojo-01.json";
import dojo02 from "../beatmaps/dojo-02.json";
import dojo03 from "../beatmaps/dojo-03.json";
import dojo04 from "../beatmaps/dojo-04.json";
import dojo05 from "../beatmaps/dojo-05.json";
import dojo06 from "../beatmaps/dojo-06.json";
import dojo07 from "../beatmaps/dojo-07.json";
import dojo08 from "../beatmaps/dojo-08.json";
import dojo09 from "../beatmaps/dojo-09.json";
import dojo10 from "../beatmaps/dojo-10.json";

export interface CampaignPhase {
  id: string;
  numeral: string;
  name: string;
  nameJP: string;
  subtitle: string;
  threshold: number;
  track: string;
  beatmap: Beatmap;
  story?: {
    speaker: "Masaru" | "Seiji";
    line: string;
  };
}

export const CAMPAIGN_PHASES: CampaignPhase[] = [
  {
    id: "dojo-01",
    numeral: "I",
    name: "Kamae",
    nameJP: "構え",
    subtitle: "Find the blade line.",
    // 31% of the 96-note full-GREAT reference clear (~32700). Early phases
    // privilege learning the movement over maintaining a long combo.
    // Reduced 30% from the original 44%/14500 baseline.
    threshold: 10000,
    track: "assets/audio/dojo-01.mp3",
    beatmap: dojo01 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "A clean stance is already a cut begun. Now learn to finish it — an honest cut needs weight behind the line, not just the line itself.",
    },
  },
  {
    id: "dojo-02",
    numeral: "II",
    name: "Kihon",
    nameJP: "基本",
    subtitle: "The honest cut — tenouchi starts to matter.",
    // 31% of the 124-note full-GREAT reference clear (~44900).
    // Reduced 30% from the original 45%/20000 baseline.
    threshold: 14000,
    track: "assets/audio/dojo-02.mp3",
    beatmap: dojo02 as Beatmap,
    story: {
      speaker: "Masaru",
      line: "Power without timing scatters before it lands. Now I have to learn what comes after it lands — the stillness, not just the strike.",
    },
  },
  {
    id: "dojo-03",
    numeral: "III",
    name: "Suburi",
    nameJP: "素振り",
    subtitle: "Follow through — hold zanshin.",
    // Track remains "Nothing wasted" at 112.3 measured BPM. The rebuilt map
    // has 150 notes, large targets and three-cut phrases so zanshin has real
    // recovery room. 31% of the full-GREAT reference clear (~54700).
    // Reduced 30% from the original 45%/24500 baseline.
    threshold: 17000,
    track: "assets/audio/dojo-03.mp3",
    beatmap: dojo03 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "Do not chase the target. Let the form remain after the strike — I chased once. It taught me nothing but wreckage. Next, learn the angle as well as the stillness.",
    },
  },
  {
    id: "dojo-04",
    numeral: "IV",
    name: "Kesa",
    nameJP: "袈裟",
    subtitle: "The diagonal — cut along the sash line.",
    // 32% of the 180-note full-GREAT reference clear (~66800).
    // Reduced 30% from the original 46%/30500 baseline.
    threshold: 21500,
    track: "assets/audio/dojo-04.mp3",
    beatmap: dojo04 as Beatmap,
    story: {
      speaker: "Masaru",
      line: "Every wrong angle leaves a mark I can feel. Seiji says the next one doesn't come from the arms at all — it comes from the ground up.",
    },
  },
  {
    id: "dojo-05",
    numeral: "V",
    name: "Kiriage",
    nameJP: "切上",
    subtitle: "The rising cut.",
    // Track remains "The rising cut_2" at 123.0 measured BPM. 33% of the
    // 199-note full-GREAT reference clear (~74800).
    // Reduced 30% from the original 47%/35000 baseline.
    threshold: 24500,
    track: "assets/audio/dojo-05.mp3",
    beatmap: dojo05 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "A rising cut begins in the ground. The arms only finish what the hips have started. Now string that beginning to an ending, and the ending to the next beginning.",
    },
  },
  {
    id: "dojo-06",
    numeral: "VI",
    name: "Renzoku",
    nameJP: "連続",
    subtitle: "Chains — no cut stands alone.",
    // Track remains "Follow through (1)" at 129.2 measured BPM. Chaining is
    // expressed through six-cut phrases, not target speed. 33% of the
    // 196-note full-GREAT reference clear (~75600).
    // Reduced 30% from the original 48%/36000 baseline.
    threshold: 25000,
    track: "assets/audio/dojo-06.mp3",
    beatmap: dojo06 as Beatmap,
    story: {
      speaker: "Masaru",
      line: "One cut ends where the next begins. I have stopped thinking between them — Seiji says that's when the form can finally hold on its own.",
    },
  },
  {
    id: "dojo-07",
    numeral: "VII",
    name: "Kata",
    nameJP: "型",
    subtitle: "The form.",
    // 34% of the 207-note full-GREAT reference clear (~78400). The seq 6
    // centerpiece raises order complexity without a sustained note wall.
    // Reduced 30% from the original 49%/38500 baseline.
    threshold: 27000,
    track: "assets/audio/dojo-07.mp3",
    beatmap: dojo07 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "When the form holds, anger has nowhere useful left to go. What's left is this: every line, every direction — nothing spared, nothing unanswered.",
    },
  },
  {
    id: "dojo-08",
    numeral: "VIII",
    name: "Happō",
    nameJP: "八方",
    subtitle: "All directions — nothing left unanswered.",
    // 35% of the 146-note full-GREAT reference clear (~54800). Medium/base
    // targets isolate eight-way flow; small/fast pressure remains phase IX.
    // Reduced 30% from the original 49%/27000 baseline.
    threshold: 19000,
    track: "assets/audio/dojo-08.mp3",
    beatmap: dojo08 as Beatmap,
    story: {
      speaker: "Masaru",
      line: "Eight lines, one breath. Nothing is left unanswered. Now Seiji wants it faster — and says speed is worth nothing if the breath breaks.",
    },
  },
  {
    id: "dojo-09",
    numeral: "IX",
    name: "Zanshin",
    nameJP: "残心",
    subtitle: "Precision under pressure.",
    // 35% of the 286-note full-GREAT reference clear (~113200). This remains
    // the first small/fast phase: precision under explicitly taught pressure.
    // Reduced 30% from the original 50%/56500 baseline.
    threshold: 39500,
    track: "assets/audio/dojo-09.mp3",
    beatmap: dojo09 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "Fast, now — but clean. Speed without form is only noise. One test remains — everything you've kept, together, at once.",
    },
  },
  {
    id: "dojo-10",
    numeral: "X",
    name: "Menkyo",
    nameJP: "免許",
    subtitle: "The graduation.",
    // 35% of the 366-note full-GREAT reference clear (~142800). The climax
    // kata (seq 7) debuts here inside bounded twelve-cut phrases.
    // Reduced 30% from the original 50%/71500 baseline.
    threshold: 50000,
    track: "assets/audio/dojo-10.mp3",
    beatmap: dojo10 as Beatmap,
    story: {
      speaker: "Seiji",
      line: "Your training is over, Masaru. Your journey begins.",
    },
  },
];

export function clampPhaseIndex(index: number): number {
  return Math.max(0, Math.min(CAMPAIGN_PHASES.length - 1, index));
}
